import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero, CTABand, Marquee } from "../components/ui-kit";
import { Reveal } from "../motion";
import { PARTNERS } from "../content";
import { PHOTOS } from "../assets";

const CHAPTERS = [
  { n: "I", h: "O Direito precisa compreender a realidade em movimento", p: "Uma decisão jurídica pode alterar uma operação inteira — impactar preços, afetar o caixa, comprometer a continuidade de um projeto ou alterar o equilíbrio de uma família. Por isso, o trabalho começa pela compreensão dos fatos, da dinâmica entre as pessoas envolvidas, da qualidade da prova e da realidade em que cada decisão produzirá efeitos." },
  { n: "II", h: "O Direito também é comunicação", p: "Uma orientação precisa ser compreendida por quem irá executá-la. Negociações dependem de uma interlocução capaz de tornar claros os interesses, os riscos e as alternativas. Em disputas, fatos, provas e fundamentos precisam ser organizados em uma argumentação clara, racional e persuasiva. A comunicação integra a própria estratégia jurídica." },
  { n: "III", h: "O consultivo e o contencioso integram um mesmo raciocínio", p: "O que é planejado, negociado e formalizado hoje poderá ser exigido, discutido ou defendido amanhã. Manter a coerência entre essas etapas é parte do trabalho — articulando Direito, tributação, contratos, tecnologia, relações de consumo e operação." },
  { n: "IV", h: "Os sócios participam diretamente do trabalho", p: "Lucas e Rodrigo participam da análise, da estratégia e da comunicação com o cliente. Essa participação integra o método do escritório e permite preservar o contexto, a responsabilidade e a continuidade do trabalho." },
];

export default function Escritorio() {
  return (
    <>
      <PageHero
        eyebrow="O escritório"
        titleLines={["Compreender o", "negócio antes de", "definir a resposta."]}
        lead="O Pahl & Burgueño Advogados atua em questões tributárias, empresariais e patrimoniais com interlocução direta dos sócios, análise individualizada e atenção aos efeitos práticos de cada decisão. A resposta jurídica é construída a partir da realidade em que a questão se apresenta."
      />

      <section className="bg-ink text-bone py-24 lg:py-32 grain overflow-hidden">
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal className="mb-16"><div className="eyebrow text-gold mb-6">Manifesto</div>
            <h2 className="display text-4xl lg:text-6xl max-w-3xl">O que orienta o trabalho.</h2></Reveal>
          <div className="space-y-px">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.06} className="grid lg:grid-cols-[0.4fr_1.6fr] gap-6 lg:gap-16 border-t border-white/12 py-10">
                <div className="serif text-gold text-6xl lg:text-7xl leading-none">{c.n}</div>
                <div>
                  <h3 className="serif text-2xl lg:text-4xl mb-5 max-w-2xl">{c.h}</h3>
                  <p className="text-bone/55 text-lg leading-relaxed font-light max-w-3xl">{c.p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      <section className="bg-bone py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal className="mb-14 max-w-3xl">
            <div className="eyebrow text-gold mb-6">Sócios-fundadores</div>
            <h2 className="display text-4xl lg:text-6xl text-ink">Duas trajetórias complementares. Um mesmo método.</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {Object.entries(PARTNERS).map(([slug, p], i) => (
              <Reveal key={slug} delay={i * 0.1}>
                <Link to={`/socios/${slug}`} data-testid={`escritorio-partner-${p.key}`}
                  className="group block bg-white border border-ink/10 rounded-2xl p-9 hover:border-gold hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="h-16 w-16 rounded-full overflow-hidden ring-1 ring-ink/10 shrink-0">
                      <img src={PHOTOS[p.key]} alt={p.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <div>
                      <h3 className="serif text-2xl text-ink group-hover:text-gold transition-colors">{p.name}</h3>
                      <p className="text-ink/50 text-sm">{p.role}</p>
                    </div>
                  </div>
                  <p className="text-ink/65 leading-relaxed font-light line-clamp-4">{p.lead}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-ink font-medium link-underline">Conhecer trajetória <ArrowRight size={16} className="text-gold" /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand title={["Apresente a questão", "ao escritório."]} text="Uma descrição inicial do assunto e de eventual prazo permite verificar a disponibilidade, possíveis conflitos de interesses e o canal adequado para continuidade." />
    </>
  );
}
